import axios, { AxiosError, AxiosInstance } from 'axios';
import type { ChainProgressEvent, NuanceSettings } from '../types';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

// Create axios instance
// Authentication is handled via httpOnly cookies (more secure than localStorage)
const api: AxiosInstance = axios.create({
  baseURL: API_BASE,
  withCredentials: true, // Include cookies in requests
  timeout: 90000, // 90 second timeout for AI generation
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Skip redirect for the initial auth check - let App.tsx handle it gracefully
      const requestUrl = error.config?.url || '';
      if (requestUrl.includes('/auth/me')) {
        return Promise.reject(error);
      }
      // Redirect to login on auth failure (cookie expired/invalid)
      // Avoid redirect loops by checking current path
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ==================== Auth ====================

export const authApi = {
  register: async (data: { email: string; password: string; name: string; company?: string }) => {
    const response = await api.post('/auth/register', data);
    // Token is set as httpOnly cookie by the server
    return response.data;
  },

  login: async (data: { email: string; password: string }) => {
    const response = await api.post('/auth/login', data);
    // Token is set as httpOnly cookie by the server
    return response.data;
  },

  logout: async () => {
    // Server clears the httpOnly cookie
    await api.post('/auth/logout');
  },

  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  changePassword: async (data: { currentPassword: string; newPassword: string }) => {
    const response = await api.post('/auth/change-password', data);
    return response.data;
  },

  forgotPassword: async (email: string) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (token: string, newPassword: string) => {
    const response = await api.post('/auth/reset-password', { token, newPassword });
    return response.data;
  },

  verifyResetToken: async (token: string) => {
    const response = await api.get('/auth/verify-reset-token', { params: { token } });
    return response.data;
  },
};

// ==================== Sessions ====================

export const sessionsApi = {
  getAll: async () => {
    const response = await api.get('/sessions');
    return response.data.sessions;
  },

  get: async (id: string) => {
    const response = await api.get(`/sessions/${id}`);
    return response.data;
  },

  create: async (data: { name: string; documentType?: string; state: any }) => {
    const response = await api.post('/sessions', data);
    return response.data;
  },

  update: async (id: string, data: { name?: string; documentType?: string; state?: any }) => {
    const response = await api.put(`/sessions/${id}`, data);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await api.delete(`/sessions/${id}`);
    return response.data;
  },
};

// ==================== Templates ====================

export const templatesApi = {
  getAll: async () => {
    const response = await api.get('/templates');
    return response.data.templates;
  },

  get: async (id: string) => {
    const response = await api.get(`/templates/${id}`);
    return response.data;
  },

  create: async (data: { name: string; sections: any[] }) => {
    const response = await api.post('/templates', data);
    return response.data;
  },

  update: async (id: string, data: { name?: string; sections?: any[] }) => {
    const response = await api.put(`/templates/${id}`, data);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await api.delete(`/templates/${id}`);
    return response.data;
  },

  // Document templates
  getAllDocumentTemplates: async () => {
    const response = await api.get('/templates/document/all');
    return response.data.templates;
  },

  getDocumentTemplate: async (id: string) => {
    const response = await api.get(`/templates/document/${id}`);
    return response.data;
  },

  createDocumentTemplate: async (data: { name: string; sections: any[] }) => {
    const response = await api.post('/templates/document', data);
    return response.data;
  },

  updateDocumentTemplate: async (id: string, data: { name?: string; sections?: any[] }) => {
    const response = await api.put(`/templates/document/${id}`, data);
    return response.data;
  },

  deleteDocumentTemplate: async (id: string) => {
    const response = await api.delete(`/templates/document/${id}`);
    return response.data;
  },
};

// ==================== AI Generation ====================

export const generateApi = {
  generate: async (data: {
    section: { id: number; title: string; prompt: string; documents?: any[]; role?: string };
    applicantData: any;
    selectedDocuments?: any[];
    llmModel?: string;
    systemPrompt?: string;
    documentContents?: { filename: string; content: string }[];
    referenceText?: string;
    styleOptions?: {
      copyTone: boolean;
      copyFormatting: boolean;
      copySentenceStructure: boolean;
      copyParagraphLength: boolean;
      copyVocabulary: boolean;
    };
    nuanceSettings?: Partial<NuanceSettings>;
  }) => {
    const response = await api.post('/generate', data);
    return response.data;
  },

  regenerateSelection: async (data: {
    fullContent: string;
    selectedText: string;
    instructions: string;
    startIndex: number;
    endIndex: number;
    sectionTitle: string;
    applicantData?: any;
    llmModel?: string;
  }) => {
    const response = await api.post('/generate/regenerate', data);
    return response.data;
  },

  chain: (
    data: {
      sections: { id: number; title: string; prompt: string; role?: string; documents?: any[] }[];
      applicantData: any;
      chainConfig: {
        contextDepth: number;
        contextFormat: 'full' | 'summary' | 'key-points';
        continueFlow: boolean;
        // Story mode options
        storyMode?: boolean;
        generateOutline?: boolean;
        trackCharacters?: boolean;
        creativityLevel?: 'low' | 'medium' | 'high';
      };
      nuanceSettings?: Partial<NuanceSettings>;
      systemPrompt?: string;
      llmModel?: string;
      referenceText?: string;
      styleOptions?: {
        copyTone: boolean;
        copyFormatting: boolean;
        copySentenceStructure: boolean;
        copyParagraphLength: boolean;
        copyVocabulary: boolean;
      };
      documentContents?: { filename: string; content: string }[];
    },
    onProgress: (event: ChainProgressEvent) => void,
    onError: (error: string) => void
  ): AbortController => {
    const controller = new AbortController();

    fetch(`${API_BASE}/generate/chain`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
      signal: controller.signal,
    }).then(response => {
      if (!response.ok) {
        response.json().then(err => onError(err.error || 'Chain generation failed')).catch(() => onError('Chain generation failed'));
        return;
      }
      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      function read() {
        reader.read().then(({ done, value }) => {
          if (done) return;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n\n');
          buffer = lines.pop() || '';
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const event = JSON.parse(line.slice(6)) as ChainProgressEvent;
                onProgress(event);
              } catch {
                // Skip malformed events
              }
            }
          }
          read();
        }).catch(err => {
          if (err.name !== 'AbortError') onError(err.message);
        });
      }
      read();
    }).catch(err => {
      if (err.name !== 'AbortError') onError(err.message);
    });

    return controller;
  },
};

// ==================== Export ====================

export const exportApi = {
  export: async (data: {
    format: 'pdf' | 'docx' | 'html';
    applicantData: any;
    sections: { title: string; content: string }[];
    documentTitle?: string;
  }) => {
    const response = await api.post('/export', data, {
      responseType: 'blob',
    });
    return response.data;
  },
};

// ==================== Cloud Storage ====================

export const cloudApi = {
  getStatus: async () => {
    const response = await api.get('/cloud/status');
    return response.data.connections;
  },

  // Google Drive
  getGoogleAuthUrl: async () => {
    const response = await api.get('/cloud/google/auth-url');
    return response.data.url;
  },

  connectGoogle: async (code: string) => {
    const response = await api.post('/cloud/google/callback', { code });
    return response.data;
  },

  getGoogleFiles: async (folderId?: string) => {
    const response = await api.get('/cloud/google/files', {
      params: { folderId },
    });
    return response.data.files;
  },

  downloadGoogleFile: async (fileId: string) => {
    const response = await api.get(`/cloud/google/download/${fileId}`);
    return response.data;
  },

  disconnectGoogle: async () => {
    const response = await api.delete('/cloud/google');
    return response.data;
  },

  // Dropbox
  getDropboxAuthUrl: async () => {
    const response = await api.get('/cloud/dropbox/auth-url');
    return response.data.url;
  },

  connectDropbox: async (code: string) => {
    const response = await api.post('/cloud/dropbox/callback', { code });
    return response.data;
  },

  getDropboxFiles: async (path?: string) => {
    const response = await api.get('/cloud/dropbox/files', {
      params: { path },
    });
    return response.data.files;
  },

  disconnectDropbox: async () => {
    const response = await api.delete('/cloud/dropbox');
    return response.data;
  },

  // OneDrive
  getOneDriveAuthUrl: async () => {
    const response = await api.get('/cloud/onedrive/auth-url');
    return response.data.url;
  },

  connectOneDrive: async (code: string) => {
    const response = await api.post('/cloud/onedrive/callback', { code });
    return response.data;
  },

  getOneDriveFiles: async (folderId?: string) => {
    const response = await api.get('/cloud/onedrive/files', {
      params: { folderId },
    });
    return response.data.files;
  },

  disconnectOneDrive: async () => {
    const response = await api.delete('/cloud/onedrive');
    return response.data;
  },
};

// ==================== User Settings ====================

export const userApi = {
  updateProfile: async (data: { name?: string; company?: string }) => {
    const response = await api.put('/users/profile', data);
    return response.data;
  },

  getSettings: async () => {
    const response = await api.get('/users/settings');
    return response.data;
  },

  updateSettings: async (data: any) => {
    const response = await api.put('/users/settings', data);
    return response.data;
  },

  getApiKeyStatus: async () => {
    const response = await api.get('/users/api-keys');
    return response.data;
  },

  setApiKey: async (provider: string, apiKey: string, baseUrl?: string) => {
    const response = await api.post('/users/api-keys', { provider, apiKey, baseUrl });
    return response.data;
  },

  deleteApiKey: async (provider: string) => {
    const response = await api.delete(`/users/api-keys/${provider}`);
    return response.data;
  },

  verifyApiKey: async (provider: string, apiKey: string, baseUrl?: string) => {
    const response = await api.post('/users/api-keys/verify', { provider, apiKey, baseUrl });
    return response.data;
  },
};

// ==================== Stripe/Subscription ====================

export const stripeApi = {
  getPrices: async () => {
    const response = await api.get('/stripe/prices');
    return response.data;
  },

  createCheckoutSession: async (planType: 'monthly' | 'annual') => {
    const response = await api.post('/stripe/create-checkout-session', { planType });
    return response.data;
  },

  createPortalSession: async () => {
    const response = await api.post('/stripe/create-portal-session', {});
    return response.data;
  },

  startTrial: async () => {
    const response = await api.post('/stripe/start-trial', {});
    return response.data;
  },
};

export default api;
